import { useState } from 'react'
import { Shield, X, Loader, AlertCircle } from 'lucide-react'
import { useAppStore } from '../store/useAppStore'
import { apiService } from '../services/myrentcarApi'
import Button from './ui/Button'

/**
 * Affiché avant chaque état des lieux.
 * Vérifie que la personne connectée est bien celle qui va faire l'EDL.
 * Propose un mini-formulaire de reconnexion si ce n'est pas elle.
 */
export default function ConfirmAgentModal({ contract, inspectionType, onConfirm, onCancel }) {
  const { credentials, setCredentials } = useAppStore()
  const [mode,     setMode]     = useState('confirm') // confirm | relogin
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState(null)

  const agentName = credentials?.username || 'Inconnu'
  const plate     = contract?.plate       || '—'
  const numContrat= contract?.numeroDocument || contract?.id || '—'
  const typeLabel = inspectionType === 'depart' ? 'départ' : 'retour'

  const handleConfirm = () => {
    onConfirm(credentials)
  }

  const handleRelogin = async () => {
    if (!username || !password) { setError('Renseignez vos identifiants'); return }
    setLoading(true)
    setError(null)
    try {
      const res = await apiService.login(
        username, password,
        credentials?.db, credentials?.societe
      )
      if (res.success) {
        // On sauvegarde le token opaque (chiffré) à la place du vrai mot de passe
        const newCreds = { ...credentials, username, password: res.token }
        setCredentials(newCreds)
        onConfirm(newCreds)
      } else {
        setError('Identifiants incorrects')
      }
    } catch (err) {
      setError('Erreur : ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

        {/* Header */}
        <div className="bg-blue-600 px-5 py-4 flex items-center gap-3">
          <Shield size={22} className="text-white flex-shrink-0"/>
          <h2 className="text-white font-black text-base flex-1">Vérification d'identité</h2>
          <button onClick={onCancel} className="text-white/70 hover:text-white">
            <X size={20}/>
          </button>
        </div>

        {mode === 'confirm' ? (
          <div className="p-5 flex flex-col gap-4">
            {/* Message */}
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                Bonjour <span className="font-black text-blue-700">{agentName}</span>, vous vous
                apprêtez à faire l'état des lieux de{' '}
                <span className="font-black text-gray-900">{typeLabel}</span> du véhicule{' '}
                <span className="font-black text-gray-900">{plate}</span>{' '}
                (contrat n°&nbsp;<span className="font-black text-gray-900">{numContrat}</span>).
              </p>
            </div>

            <p className="text-sm text-gray-500 text-center">
              Si ce n'est pas vous, reconnectez-vous avec vos identifiants.
            </p>

            {/* Boutons */}
            <Button onClick={handleConfirm} size="lg" className="w-full">
              ✓ C'est bien moi, continuer
            </Button>
            <button
              onClick={() => setMode('relogin')}
              className="text-sm text-blue-600 font-semibold text-center w-full py-1 hover:underline"
            >
              Ce n'est pas moi — me connecter
            </button>
          </div>

        ) : (
          <div className="p-5 flex flex-col gap-4">
            <p className="text-sm text-gray-600">
              Entrez vos identifiants pour prendre en charge cet état des lieux.
            </p>

            <input
              type="text"
              placeholder="Identifiant"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <input
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRelogin()}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl px-3 py-2">
                <AlertCircle size={15}/> {error}
              </div>
            )}

            <Button onClick={handleRelogin} disabled={loading} size="lg" className="w-full">
              {loading ? <><Loader size={16} className="animate-spin"/> Connexion...</> : 'Se connecter'}
            </Button>
            <button
              onClick={() => { setMode('confirm'); setError(null) }}
              className="text-sm text-gray-500 text-center w-full py-1 hover:underline"
            >
              ← Retour
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
